import React, { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, ArrowRight, CalendarClock, Loader2, MapPin } from 'lucide-react';
import { toast } from 'sonner';
import { Kicker, SmchsFooter, SmchsNav, SmchsStyleBlock } from '@/components/smchs/Shell';
import { CollectionStyle, GrainOverlay, Reveal } from '@/components/smchs/Collection';
import { apiErrorMessage, timeAgo } from '@/components/smchs/discussion-lib';
import {
  bookclubClubsApi,
  bookclubDiscussionsApi,
  bookclubMeetingsApi,
  bookclubMembershipApi,
} from '@/services/bookclub-api';
import { TokenManager } from '@/lib/token-manager';

/* =====================================================================
 * CLUB: one book club. Who runs it, when it meets, what it is reading,
 * and every thread in it. This is the destination for "click a club",
 * and each thread from here opens its own page.
 *
 * The faculty advisor is a plain text label on the club (advisor_name),
 * not a linked account: see the club header note.
 * ===================================================================== */

const shell = 'max-w-[1040px] mx-auto px-5 md:px-8';

const clubKeys = {
  detail: (slug: string) => ['bookclub', 'club', slug] as const,
  threads: (slug: string) => ['bookclub', 'club-threads', slug] as const,
  meetings: (slug: string) => ['bookclub', 'club-meetings', slug] as const,
};

const ClubPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  return (
    <div className="smchs-home">
      <SmchsStyleBlock />
      <CollectionStyle />
      <SmchsNav />
      <main className="relative">
        {slug ? <ClubContent slug={slug} /> : <NotFoundBlock />}
      </main>
      <SmchsFooter />
      <GrainOverlay />
    </div>
  );
};

const BackLink: React.FC = () => (
  <Link
    to="/book-clubs"
    className="mt-10 inline-flex items-center gap-1.5 text-[13px] font-medium text-[var(--ink)] hover:text-[var(--primary)] transition-colors"
  >
    <ArrowLeft className="w-3.5 h-3.5" strokeWidth={1.75} /> All book clubs
  </Link>
);

const NotFoundBlock: React.FC = () => (
  <section className={`${shell} pt-16 md:pt-20 pb-24`}>
    <h1 className="font-display font-[900] text-[2rem] md:text-[2.6rem] leading-[1.05] tracking-[-0.025em] text-[var(--ink)]">
      That club is not here.
    </h1>
    <p className="mt-4 text-[15px] leading-[1.6] text-[var(--ink-soft)] max-w-[46ch]">
      It may have been archived, or the link may be wrong.
    </p>
    <BackLink />
  </section>
);

const ClubContent: React.FC<{ slug: string }> = ({ slug }) => {
  const qc = useQueryClient();
  const signedIn = TokenManager.isTokenValid();
  const [joining, setJoining] = useState(false);

  const {
    data: club,
    isLoading,
    isError,
  } = useQuery({
    queryKey: clubKeys.detail(slug),
    queryFn: () => bookclubClubsApi.getClub(slug),
    staleTime: 60 * 1000,
    retry: false,
  });

  const { data: threadData } = useQuery({
    queryKey: clubKeys.threads(slug),
    queryFn: () =>
      bookclubDiscussionsApi.getDiscussions({ club: slug, page_size: 20 }),
    staleTime: 60 * 1000,
  });

  const { data: meetingData } = useQuery({
    queryKey: clubKeys.meetings(slug),
    queryFn: () => bookclubMeetingsApi.getMeetings({ club: slug, days: 60, page_size: 5 }),
    staleTime: 5 * 60 * 1000,
  });

  const threads = threadData?.results ?? [];
  const nextMeeting = meetingData?.results?.[0];

  const toggleMembership = async () => {
    if (!club) return;
    setJoining(true);
    try {
      if (club.is_member) {
        await bookclubMembershipApi.leaveClub(slug);
        toast.success(`Left ${club.name}`);
      } else {
        await bookclubMembershipApi.joinClub(slug);
        toast.success(`Joined ${club.name}`);
      }
      qc.invalidateQueries({ queryKey: clubKeys.detail(slug) });
      qc.invalidateQueries({ queryKey: ['student', 'desk', 'clubs'] });
      qc.invalidateQueries({ queryKey: ['bookclub', 'clubs', 'for-thread'] });
    } catch (e) {
      toast.error(apiErrorMessage(e, 'Could not update your membership'));
    } finally {
      setJoining(false);
    }
  };

  if (isLoading) {
    return (
      <section className={`${shell} pt-16 md:pt-20 pb-24`}>
        <div className="h-4 w-28 rounded bg-[var(--surface-2)] animate-pulse" />
        <div className="mt-6 h-10 w-2/3 rounded bg-[var(--surface-2)] animate-pulse" />
        <div className="mt-10 space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-16 rounded bg-[var(--surface-2)] animate-pulse" />
          ))}
        </div>
      </section>
    );
  }

  if (isError || !club) return <NotFoundBlock />;

  return (
    <>
      <section className="border-b border-[var(--border)]">
        <div className={`${shell} pt-12 md:pt-16 pb-8 md:pb-10`}>
          <Kicker label={club.tag} />
          <div className="mt-5 flex flex-wrap items-start justify-between gap-6">
            <div className="min-w-0">
              <h1 className="font-display font-[900] text-[2.1rem] sm:text-[2.6rem] md:text-[3rem] leading-[1.05] tracking-[-0.03em] text-[var(--ink)]">
                {club.name}
              </h1>
              <p className="mt-4 text-[15px] leading-[1.6] text-[var(--ink-soft)] max-w-[54ch]">
                {club.description}
              </p>
            </div>
            {signedIn && (
              <button
                onClick={toggleMembership}
                disabled={joining}
                className={
                  club.is_member
                    ? 'shrink-0 px-4 py-2.5 rounded-lg border border-[var(--border)] text-[13.5px] font-medium text-[var(--ink)] hover:border-[var(--primary)] hover:text-[var(--primary)] active:translate-y-px transition-all disabled:opacity-40'
                    : 'shrink-0 px-4 py-2.5 rounded-lg bg-[var(--ink)] text-[var(--paper)] text-[13.5px] font-medium hover:bg-[var(--primary)] active:translate-y-px transition-all disabled:opacity-40'
                }
              >
                {joining ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin mx-auto" />
                ) : club.is_member ? (
                  'Leave club'
                ) : (
                  'Join club'
                )}
              </button>
            )}
          </div>

          <dl className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-y-5 gap-x-6">
            <Fact label="Faculty advisor" value={club.advisor_name || 'Not set'} />
            <Fact label="Meets" value={club.schedule_text} />
            <Fact label="Where" value={club.location} />
            <Fact
              label="Members"
              value={`${club.member_count}${club.my_role === 'leader' ? ' · you lead' : ''}`}
            />
          </dl>
        </div>
      </section>

      <section className={`${shell} pt-10 md:pt-14 pb-6`}>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-5 items-start">
          {club.current_book && (
            <Reveal className="lg:col-span-7">
              <div className="rounded-md border border-[var(--border)] bg-[var(--surface)] p-6 md:p-7">
                <div className="text-[14.5px] font-semibold text-[var(--ink)] mb-4">
                  Reading now
                </div>
                <div className="font-serif italic text-[1.4rem] leading-[1.25] text-[var(--ink)] pb-1">
                  {club.current_book.title}
                </div>
                <div className="mt-1 text-[13px] text-[var(--ink-soft)]">
                  {club.current_book.author}
                </div>
                <div className="mt-5 h-[3px] rounded-full bg-[var(--border)] overflow-hidden">
                  <div
                    className="h-full rounded-full bg-[var(--gold)]"
                    style={{ width: `${Math.min(100, club.current_book.progress)}%` }}
                  />
                </div>
                <div className="mt-2.5 font-mono text-[10.5px] uppercase tracking-[0.16em] text-[var(--muted)]">
                  {club.current_book.progress}% through
                </div>
              </div>
            </Reveal>
          )}

          {nextMeeting && (
            <Reveal delay={0.06} className={club.current_book ? 'lg:col-span-5' : 'lg:col-span-7'}>
              <div className="rounded-md border border-[var(--border)] bg-[var(--surface)] p-6 md:p-7">
                <div className="text-[14.5px] font-semibold text-[var(--ink)] mb-4">
                  Next meeting
                </div>
                <div className="font-medium text-[15px] leading-snug text-[var(--ink)]">
                  {nextMeeting.topic}
                </div>
                <div className="mt-3 flex flex-wrap items-center gap-x-3.5 gap-y-1 font-mono text-[11px] uppercase tracking-[0.12em] text-[var(--muted)]">
                  <span className="inline-flex items-center gap-1.5">
                    <CalendarClock className="w-3.5 h-3.5" strokeWidth={1.75} />
                    {new Date(`${nextMeeting.date}T00:00:00`).toLocaleDateString('en-GB', {
                      day: 'numeric',
                      month: 'short',
                    })}
                  </span>
                  <span className="inline-flex items-center gap-1.5">
                    <MapPin className="w-3.5 h-3.5" strokeWidth={1.75} />
                    {nextMeeting.location}
                  </span>
                </div>
              </div>
            </Reveal>
          )}
        </div>
      </section>

      <section className={`${shell} pt-8 md:pt-10 pb-20 md:pb-24`}>
        <h2 className="font-display font-[900] text-[1.7rem] md:text-[2rem] leading-[1.05] tracking-[-0.025em] text-[var(--ink)]">
          Threads.
        </h2>

        {threads.length === 0 ? (
          <p className="mt-6 text-[14.5px] leading-[1.6] text-[var(--ink-soft)] max-w-[48ch]">
            No one has started a thread in this club yet.
          </p>
        ) : (
          <Reveal>
            <div className="mt-6 rounded-md border border-[var(--border)] bg-[var(--surface)] overflow-hidden">
              {threads.map((t) => (
                <Link
                  key={t.id}
                  to={`/book-clubs/threads/${t.id}`}
                  className="group flex items-start gap-4 px-5 md:px-6 py-4 border-b border-[var(--border)] last:border-0 hover:bg-[var(--surface-2)]/60 transition-colors"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-[15px] leading-snug text-[var(--ink)] group-hover:text-[var(--primary)] transition-colors">
                        {t.title}
                      </span>
                      {t.is_pinned && (
                        <span className="shrink-0 text-[9px] font-mono uppercase tracking-[0.14em] px-1.5 py-0.5 rounded-full bg-[var(--gold)]/15 text-[var(--gold-text)]">
                          Pinned
                        </span>
                      )}
                    </div>
                    <div className="mt-1.5 flex flex-wrap items-center gap-x-3.5 gap-y-1 font-mono text-[11px] uppercase tracking-[0.12em] text-[var(--muted)]">
                      <span>{t.author_name}</span>
                      <span>
                        {t.reply_count} {t.reply_count === 1 ? 'reply' : 'replies'}
                      </span>
                      <span>{timeAgo(t.last_reply_at ?? t.created_at)}</span>
                    </div>
                  </div>
                  <ArrowRight
                    className="w-4 h-4 mt-1 shrink-0 text-[var(--muted)] group-hover:text-[var(--ink)] group-hover:translate-x-0.5 transition-all"
                    strokeWidth={1.75}
                  />
                </Link>
              ))}
            </div>
          </Reveal>
        )}

        <BackLink />
      </section>
    </>
  );
};

const Fact: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div>
    <dt className="font-mono text-[10px] uppercase tracking-[0.18em] text-[var(--muted)]">
      {label}
    </dt>
    <dd className="mt-1.5 text-[14px] text-[var(--ink)]">{value}</dd>
  </div>
);

export default ClubPage;
